import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Truck, List, SignOut, Leaf, ArrowLeft, Bell, Storefront, Gear, Package, X, Printer, Scales, UsersThree } from "@phosphor-icons/react";
import { useStaffAuth } from "../../lib/staffAuth";
import { staffApi, getGps } from "../../lib/staffApi";
import StaffSecuritySettings from "./StaffSecuritySettings";
import LocationGate from "../../lib/LocationGate";

export default function StaffLayout() {
  const { staff, logout, ready } = useStaffAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [securityOpen, setSecurityOpen] = useState(false);

  const loadUnread = useCallback(async () => {
    if (!staff) return;
    try {
      const r = await staffApi.get("/staff-app/notifications/unread-count");
      setUnread(r.data?.unread || 0);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[staff] unread poll failed:", err?.message || err);
      }
    }
  }, [staff]);

  const openBell = async () => {
    setNotifOpen(true);
    try {
      const r = await staffApi.get("/staff-app/notifications?limit=30");
      setNotifs(r.data || []);
    } catch { setNotifs([]); }
  };

  const markAll = async () => {
    try {
      await staffApi.post("/staff-app/notifications/mark-all-read", {});
      setUnread(0);
      setNotifs(ns => ns.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  // Periodic GPS ping (every 60s) if present
  useEffect(() => {
    if (!staff) return;
    let timer;
    const doPing = async () => {
      try {
        const gps = await getGps({ timeout: 5000, maximumAge: 30000 });
        await staffApi.post("/staff-app/ping", {
          latitude: gps.latitude, longitude: gps.longitude, activity: "active",
        });
      } catch (err) {
        // Background GPS ping — silent by design (permission denied / offline).
        // Log at debug so devs can trace without user-visible spam.
        if (process.env.NODE_ENV !== "production") {
          console.debug("[staff] gps ping skipped:", err?.message || err);
        }
      }
    };
    timer = setInterval(doPing, 60000);
    return () => clearInterval(timer);
  }, [staff]);

  // Unread notification count — poll every 30s (item 3)
  useEffect(() => {
    if (!staff) return;
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, [staff, loadUnread]);

  if (!ready) return null;
  if (!staff) return <Navigate to="/staff-app/login" replace />;

  const onTaskRoute = /^\/staff-app\/tasks\/[^/]+/.test(loc.pathname);

  return (
    <LocationGate
      pingPath="/api/staff-app/ping"
      tokenKey="rescigo_staff_token"
      bodyShape="staff"
      staffId={staff.id}
      label="Rescigo Field"
      required
      onRevoked={() => { try { logout?.(); } catch { /* noop */ } }}
    >
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="staff-shell">
      <header className="sticky top-0 z-20 bg-[#0F1F0C] text-white">
        <div className="max-w-lg mx-auto flex items-center gap-3 px-4 h-14">
          {onTaskRoute ? (
            <button onClick={() => nav("/staff-app/tasks")} className="p-1.5 -ml-1.5 hover:bg-white/10 rounded" data-testid="staff-back">
              <ArrowLeft size={18} />
            </button>
          ) : (
            <img src="/brand/rescigo-mark-128.png" alt="Rescigo"
                  className="w-8 h-8 rounded-md object-contain bg-white p-1 ring-1 ring-white/20"/>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 leading-none">Field staff</div>
            <div className="font-heading text-[15px] leading-tight truncate" data-testid="staff-name">{staff.name}</div>
          </div>
          <button
            data-testid="staff-bell"
            onClick={openBell}
            className="relative p-2 rounded hover:bg-white/10 text-white/80"
            title="Notifications"
          >
            <Bell size={18} />
            {unread > 0 && (
              <span data-testid="staff-bell-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C25D38] text-[10px] font-bold flex items-center justify-center">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
          <button
            data-testid="staff-security"
            onClick={() => setSecurityOpen(true)}
            className="p-2 rounded hover:bg-white/10 text-white/80"
            title="Security · PIN & Biometric"
          ><Gear size={18} /></button>
          <button
            data-testid="staff-printer"
            onClick={() => nav("/staff-app/printer")}
            className="p-2 rounded hover:bg-white/10 text-white/80"
            title="Bluetooth printer settings"
          ><Printer size={18} /></button>
          <button
            data-testid="staff-scale"
            onClick={() => nav("/staff-app/scale")}
            className="p-2 rounded hover:bg-white/10 text-white/80"
            title="Weight scale settings"
          ><Scales size={18} /></button>
          <button
            data-testid="staff-logout"
            onClick={async () => { await logout(); nav("/staff-app/login"); }}
            className="p-2 rounded hover:bg-white/10 text-white/80"
            title="Sign out"
          ><SignOut size={18} /></button>
        </div>
      </header>

      {securityOpen && <StaffSecuritySettings onClose={() => setSecurityOpen(false)} />}

      {notifOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-start justify-center pt-16 px-4"
             onClick={() => setNotifOpen(false)} data-testid="staff-bell-panel">
          <div className="w-full max-w-lg bg-white rounded-lg shadow-lg max-h-[70vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center justify-between gap-2">
              <div className="font-heading text-sm">Notifications</div>
              <div className="flex items-center gap-3">
                <button onClick={markAll} data-testid="staff-bell-markall"
                        className="text-[11px] text-[#2D5A27] hover:underline">Mark all read</button>
                <button onClick={() => setNotifOpen(false)} data-testid="staff-bell-close-x"
                        aria-label="Close notifications"
                        className="w-7 h-7 flex items-center justify-center rounded-full text-[#5C5A55] hover:bg-[#F2F0E9] hover:text-[#1A1A1A] transition-colors">
                  <X size={16} weight="bold" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-[#F2F0E9]">
              {notifs.length === 0 && (
                <div className="p-8 text-center text-sm text-[#8A8780]">No notifications yet</div>
              )}
              {notifs.map(n => (
                <div key={n.id} data-testid={`staff-notif-${n.id}`}
                     className={`px-4 py-3 text-sm ${n.read_at ? "opacity-70" : "bg-[#F7F6F2]"}`}>
                  <div className="font-medium text-[13px]">{n.subject}</div>
                  <div className="text-[12px] text-[#5C5A55] mt-0.5">{n.body}</div>
                  <div className="text-[10px] text-[#8A8780] uppercase tracking-wider mt-1">
                    {(n.kind || "").replace(/_/g, " ")} · {(n.created_at || "").slice(0, 19).replace("T", " ")}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 border-t border-[#E5E2D9] text-right">
              <button onClick={() => setNotifOpen(false)} data-testid="staff-bell-close"
                      className="text-[11px] text-[#5C5A55]">Close</button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-lg mx-auto px-4 py-5 pb-24">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E2D9] z-20">
        <div className="max-w-lg mx-auto grid grid-cols-5">
          <Tab to="/staff-app" end label="Duty" icon={Leaf} />
          <Tab to="/staff-app/tasks" label="Tasks" icon={List} />
          <Tab to="/staff-app/team" label="Team" icon={UsersThree} />
          <Tab to="/staff-app/deliveries" label="Deliveries" icon={Package} />
          <Tab to="/staff-app/pos" label="POS" icon={Storefront} />
        </div>
      </nav>
    </div>
    </LocationGate>
  );
}

const Tab = ({ to, end, label, icon: Icon }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={`staff-tab-${label.toLowerCase().replace(" ", "-")}`}
    className={({ isActive }) =>
      `flex flex-col items-center gap-0.5 py-3 text-[10px] uppercase tracking-[0.18em] ${
        isActive ? "text-[#2D5A27]" : "text-[#8A8780]"
      }`
    }
  >
    {({ isActive }) => (
      <>
        <Icon size={20} weight={isActive ? "fill" : "duotone"} />
        <span>{label}</span>
      </>
    )}
  </NavLink>
);
